import type { FeedEntry } from '~/services/layout/index.ts';
import { isRecord } from '~/services/layout/layout.ts';

export const BLOCKED_POWER_CELL_RIGHT_CLICK_ALIASES = new Set([
  'open_queue',
  'go_to_queue',
]);

/**
 * Extracts a normalized action alias from multiple supported shapes.
 * Falls back across `button.alias`, `alias`, and `action_alias`.
 */
export function getActionAlias(action: unknown): string | undefined {
  if (!isRecord(action)) {
    return undefined;
  }

  const button = isRecord(action.button) ? action.button : undefined;
  const aliasSource = button?.alias ?? action.alias ?? action.action_alias;

  if (typeof aliasSource !== 'string') {
    return undefined;
  }

  const normalizedAlias = aliasSource.trim().toLowerCase();

  return normalizedAlias.length > 0 ? normalizedAlias : undefined;
}

/**
 * Builds contextual actions for the PowerCell right-click menu.
 * Filters queue-open aliases that are not actionable when the player is closed,
 * while preserving other actions and original ordering.
 */
export function buildPowerCellRightClickContextualEntry({
  entry,
}: {
  entry: FeedEntry;
}): {
  entry: FeedEntry;
  hasRenderableActions: boolean;
} {
  const contextualActions = entry?.extensions?.entry_action;

  if (!Array.isArray(contextualActions)) {
    return {
      entry,
      hasRenderableActions: false,
    };
  }

  const filteredContextualActions = contextualActions.filter((action) => {
    const actionAlias = getActionAlias(action);

    if (!actionAlias) {
      return true;
    }

    return !BLOCKED_POWER_CELL_RIGHT_CLICK_ALIASES.has(actionAlias);
  });

  if (filteredContextualActions.length === 0) {
    return {
      entry: {
        ...entry,
        extensions: {
          ...(entry.extensions || {}),
          entry_action: filteredContextualActions,
        },
      } as FeedEntry,
      hasRenderableActions: false,
    };
  }

  if (filteredContextualActions.length === contextualActions.length) {
    return {
      entry,
      hasRenderableActions: true,
    };
  }

  return {
    entry: {
      ...entry,
      extensions: {
        ...(entry.extensions || {}),
        entry_action: filteredContextualActions,
      },
    } as FeedEntry,
    hasRenderableActions: true,
  };
}
